import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./App.css";
import "leaflet/dist/leaflet.css";
import Map, { MapRef } from "./components/Map";
import InfoSidebar from "./components/InfoSidebar";
import NationalDashboard from "./components/NationalDashboard";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import { Municipality } from "./types";
import {
  loadBudgetData,
  findBudgetByMatchKey,
} from "./services/budgetDataService";

// Standalone Analytics page component for /analytics route
const AnalyticsPage: React.FC = () => {
  return (
    <div style={{ height: "100vh", backgroundColor: "#f5f5f5" }}>
      <AnalyticsDashboard />
    </div>
  );
};

const App: React.FC = () => {
  const location = useLocation();
  
  // If we're on /analytics route, render only the analytics page
  if (location.pathname === "/analytics") {
    return <AnalyticsPage />;
  }

  return <MainApp />;
};

const MainApp: React.FC = () => {
  const [selectedMunicipality, setSelectedMunicipality] =
    useState<Municipality | null>(null);
  const [activePage, setActivePage] = useState<
    "map" | "election" | "dashboard"
  >("map");
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [currentLogoIndex, setCurrentLogoIndex] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<number>(2567); // Budget year state
  const mapRef = useRef<MapRef>(null);

  // Array of logo images to rotate
  const logoImages = ["/1.png", "/2.png", "/3.png"];

  // Update selected municipality when year changes
  useEffect(() => {
    const updateMunicipalityBudget = async () => {
      setSelectedMunicipality((currentMunicipality) => {
        if (!currentMunicipality) return null;

        // Fetch new budget data asynchronously
        loadBudgetData(selectedYear)
          .then((budgetData) => {
            const budget = findBudgetByMatchKey(
              budgetData,
              currentMunicipality.id
            );

            if (budget) {
              setSelectedMunicipality({
                ...currentMunicipality,
                budget: budget.totalRevenue * 1000000, // Convert from million baht to baht
                population: budget.population || 0,
                area: budget.area || 0,
                budgetSources: {
                  selfCollected: budget.selfCollected,
                  stateAllocated: budget.stateAllocated,
                  subsidies: budget.subsidies,
                },
              });
            }
          })
          .catch((error) => {
            console.error("Error updating municipality budget:", error);
          });

        return currentMunicipality;
      });
    };

    updateMunicipalityBudget();
  }, [selectedYear]);

  // Function to handle municipality selection
  const handleMunicipalitySelect = (municipality: Municipality | null) => {
    setSelectedMunicipality(municipality);
  };

  // Function to handle municipality selection from search, which also focuses the map
  const handleMunicipalitySelectFromSearch = (municipality: Municipality) => {
    setSelectedMunicipality(municipality);
    setActivePage("map"); // Switch to map view
    // Focus map on the selected municipality
    if (mapRef.current) {
      mapRef.current.focusOnMunicipality(municipality.id);
    }
  };

  const handleRandomSelect = async () => {
    try {
      const budgetData = await loadBudgetData(2567);
      if (budgetData.length > 0) {
        const random =
          budgetData[Math.floor(Math.random() * budgetData.length)];

        // Construct Municipality object
        const municipality: Municipality = {
          id: random.matchKey,
          name: random.name,
          province: random.province,
          district: random.district,
          type: random.name.includes("อบต")
            ? "องค์การบริหารส่วนตำบล"
            : "เทศบาล",
          budget: random.totalRevenue * 1000000,
          population: random.population,
          area: random.area,
          budgetSources: {
            selfCollected: random.selfCollected,
            stateAllocated: random.stateAllocated,
            subsidies: random.subsidies,
          },
        };

        handleMunicipalitySelectFromSearch(municipality);
      }
    } catch (error) {
      console.error("Error selecting random municipality:", error);
    }
  };

  // Logo rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex((prevIndex) => (prevIndex + 1) % logoImages.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const navControls = document.querySelector(".nav-controls");
      if (menuOpen && navControls && !navControls.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div
            className="logo-container"
            onClick={() => setActivePage("map")}
            style={{ cursor: "pointer" }}
          >
            <img
              src={logoImages[currentLogoIndex]}
              alt="อบต.ใกล้ฉัน Logo"
              className="app-logo"
            />
          </div>

          <div className="nav-controls">
            <div
              className={`nav-container ${menuOpen ? "open" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={`nav-button ${activePage === "map" ? "active" : ""}`}
                onClick={() => {
                  setActivePage("map");
                  setMenuOpen(false);
                }}
                aria-label="หน้าแรก"
              >
                หน้าแรก
              </button>
              <button
                className={`nav-button ${
                  activePage === "dashboard" ? "active" : ""
                }`}
                onClick={() => {
                  setActivePage("dashboard");
                  setMenuOpen(false);
                }}
                aria-label="วิเคราะห์ข้อมูล"
              >
                วิเคราะห์ข้อมูล
              </button>
              <button
                className={`nav-button ${
                  activePage === "election" ? "active" : ""
                }`}
                onClick={() => {
                  setActivePage("election");
                  setMenuOpen(false);
                }}
                aria-label="ที่มาข้อมูล"
              >
                ที่มาข้อมูล
              </button>
            </div>

            <button
              className="menu-toggle"
              onClick={toggleMenu}
              onTouchStart={(e) => e.stopPropagation()}
              aria-label={menuOpen ? "ปิดเมนู" : "เปิดเมนู"}
              aria-expanded={menuOpen}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                {menuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      {activePage === "dashboard" ? (
        <NationalDashboard
          onMunicipalitySelect={handleMunicipalitySelectFromSearch}
          onRandomSelect={handleRandomSelect}
        />
      ) : activePage === "map" ? (
        <div className="app-container">
          <InfoSidebar
            municipality={selectedMunicipality}
            onMunicipalitySelect={handleMunicipalitySelectFromSearch}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
          <div className="main-content">
            <Map
              ref={mapRef}
              onMunicipalitySelect={handleMunicipalitySelect}
              selectedYear={selectedYear}
            />
          </div>
        </div>
      ) : (
        <div className="data-sources-page">
          <div className="data-sources-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="header-icon"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
            <h2>ที่มาข้อมูล</h2>
            <p className="data-sources-description">
              ข้อมูลที่ใช้ในเว็บไซต์นี้มาจากแหล่งข้อมูลที่เชื่อถือได้ของหน่วยงานราชการไทย
            </p>
          </div>

          <div className="data-sources-content">
            <div className="data-source-card">
              <div className="source-number">1</div>
              <div className="source-details">
                <h3>งบประมาณรายได้ อบต.</h3>
                <p className="source-provider">
                  กรมส่งเสริมการปกครองท้องถิ่น (สถ.)
                </p>
                <p className="source-description">
                  ข้อมูลงบประมาณรายได้ขององค์การบริหารส่วนตำบลทั่วประเทศ
                  รวมถึงรายละเอียดแหล่งที่มาของรายได้ ทั้งจัดเก็บเอง รัฐจัดสรร
                  และเงินอุดหนุน
                </p>
                <a
                  href="https://opendata.dla.go.th/dataset/2560-2567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link-btn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  เยี่ยมชมเว็บไซต์
                </a>
              </div>
            </div>

            <div className="data-source-card">
              <div className="source-number">2</div>
              <div className="source-details">
                <h3>เขตสำรวจรระดับองค์กรปกครองส่วนท้องถิ่น</h3>
                <p className="source-provider">สำนักงานสถิติแห่งชาติ</p>
                <p className="source-description">
                  ข้อมูลภูมิศาสตร์และขอบเขตพื้นที่ขององค์การบริหารส่วนตำบลทั่วประเทศ
                  ในรูปแบบ GeoJSON เพื่อแสดงผลบนแผนที่
                </p>
                <a
                  href="https://esurvey.nso.go.th/arcgis/rest/services/Hosted/%E0%B9%80%E0%B8%82%E0%B8%95%E0%B8%AA%E0%B8%B3%E0%B8%A3%E0%B8%A7%E0%B8%88%E0%B8%A3%E0%B8%A3%E0%B8%B0%E0%B8%94%E0%B8%B1%E0%B8%9A%E0%B8%AD%E0%B8%87%E0%B8%84%E0%B9%8C%E0%B8%81%E0%B8%A3%E0%B8%9B%E0%B8%81%E0%B8%84%E0%B8%A3%E0%B8%AD%E0%B8%87%E0%B8%AA%E0%B9%88%E0%B8%A7%E0%B8%99%E0%B8%97%E0%B9%89%E0%B8%AD%E0%B8%87%E0%B8%96%E0%B8%B4%E0%B9%88%E0%B8%99/FeatureServer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link-btn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  เยี่ยมชมเว็บไซต์
                </a>
              </div>
            </div>

            <div className="data-source-card">
              <div
                className="source-number"
                style={{
                  background:
                    "linear-gradient(135deg, var(--tertiary) 0%, #4a37c9 100%)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="source-details">
                <h3>ผู้จัดทำโครงการ</h3>
                <p className="source-provider">PI-Public Intelligence</p>
                <p className="source-description">
                  PI คือทีมใหม่ของสำนักเครือข่ายและการมีส่วนร่วมสาธารณะ
                  ไทยพีบีเอส
                  โดยเป้าหมายของทีมคือการสร้างการมีส่วนร่วมที่ผ่านการไตร่ตรองของประชาชนในประเด็นสาธารณะ
                </p>
                <a
                  href="https://www.facebook.com/profile.php?id=100092195477858"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link-btn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                  ติดตามผลงานของเรา
                </a>
              </div>
            </div>

            <div className="data-usage-notice">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <div>
                <h4>หมายเหตุการใช้ข้อมูล</h4>
                <p>
                  ข้อมูลทั้งหมดในเว็บไซต์นี้เป็นข้อมูลสาธารณะที่เผยแพร่โดยหน่วยงานราชการ
                  และนำมาประมวลผลเพื่อให้ประชาชนเข้าถึงได้ง่าย
                  หากพบข้อมูลที่ไม่ถูกต้อง กรุณาแจ้งให้เราทราบ
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
